import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useConfirmDialog } from '../components/ConfirmDialog';
import api from '../utils/api';
import './Calendar.css';

type ScheduleParticipant = {
  name: string;
  profilePicture: string | null;
  responseStatus: 'pending' | 'accepted' | 'rejected' | 'expired' | 'superseded';
  userId: number;
};

type ScheduleItem = {
  acceptedCount: number;
  canDeleteManual: boolean;
  canRequestCancellation: boolean;
  canRespond: boolean;
  createdAt: string;
  groupId: number | null;
  isOwner: boolean;
  kind: 'group' | 'manual';
  manualEventId: number | null;
  myResponseStatus: 'owner' | 'pending' | 'accepted' | 'rejected' | 'expired' | 'superseded' | null;
  participants: ScheduleParticipant[];
  pendingCount: number;
  reasonText: string | null;
  rejectedBy: ScheduleParticipant[];
  requestId: number | null;
  roleLabels: string[];
  sceneHeading: string | null;
  sceneId: number | null;
  sceneNumber: number | null;
  sceneTitle: string;
  scheduledFor: string;
  scheduledForLabel: string;
  status: 'pending' | 'accepted' | 'rejected' | 'superseded' | 'manual';
  storyId: number | null;
  storyTitle: string;
  updatedAt: string;
};

type CalendarResponse = {
  occupiedDates: string[];
  schedules: ScheduleItem[];
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const IST = 'Asia/Kolkata';

const formatMonthLabel = (date: Date) =>
  new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(date);

const formatIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (isoDate: string) =>
  new Intl.DateTimeFormat('en-GB').format(new Date(`${isoDate}T00:00:00`));

const getIndiaTodayIso = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const getCalendarCells = (month: Date) => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const firstWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: Array<{ date: Date; inMonth: boolean }> = [];

  for (let index = 0; index < 42; index += 1) {
    const dayOffset = index - firstWeekday + 1;
    const cellDate = new Date(year, monthIndex, dayOffset);
    cells.push({
      date: cellDate,
      inMonth: cellDate.getMonth() === monthIndex,
    });
  }

  if (daysInMonth <= 28 && cells.slice(35).every((cell) => !cell.inMonth)) {
    return cells.slice(0, 35);
  }

  return cells;
};

const getStatusLabel = (schedule: ScheduleItem) => {
  if (schedule.kind === 'manual') {
    return 'MANUAL BLOCK';
  }

  if (schedule.status === 'pending') {
    return 'WAITING FOR ALL';
  }

  return schedule.status.toUpperCase();
};

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedRequestId = Number(searchParams.get('requestId') || 0);
  const { confirm, confirmDialog } = useConfirmDialog();

  const [data, setData] = useState<CalendarResponse>({ occupiedDates: [], schedules: [] });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');
  const [respondingRequestId, setRespondingRequestId] = useState<number | null>(null);
  const [cancellingRequestId, setCancellingRequestId] = useState<number | null>(null);
  const [savingManual, setSavingManual] = useState(false);
  const [deletingManualId, setDeletingManualId] = useState<number | null>(null);
  const [manualReason, setManualReason] = useState('');

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<CalendarResponse>('/schedules/calendar');
      const nextData = {
        occupiedDates: response.data.occupiedDates || [],
        schedules: response.data.schedules || [],
      };

      setData(nextData);

      if (highlightedRequestId) {
        const highlighted = nextData.schedules.find((schedule) => schedule.requestId === highlightedRequestId);
        if (highlighted) {
          const highlightDate = new Date(`${highlighted.scheduledFor}T00:00:00`);
          setVisibleMonth(new Date(highlightDate.getFullYear(), highlightDate.getMonth(), 1));
          setSelectedDate(highlighted.scheduledFor);
        }
      }
    } catch (loadError: any) {
      setError(loadError?.response?.data?.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [highlightedRequestId]);

  useEffect(() => {
    loadCalendar().catch(() => {});
  }, [loadCalendar]);

  useEffect(() => {
    if (!banner) return undefined;
    const timer = window.setTimeout(() => setBanner(''), 3200);
    return () => window.clearTimeout(timer);
  }, [banner]);

  const todayIso = useMemo(() => getIndiaTodayIso(), []);
  const monthCells = useMemo(() => getCalendarCells(visibleMonth), [visibleMonth]);
  const dayStatusByDate = useMemo(() => {
    const nextMap = new Map<string, 'vacant' | 'waiting' | 'occupied'>();

    data.occupiedDates.forEach((date) => {
      nextMap.set(date, 'occupied');
    });

    data.schedules.forEach((schedule) => {
      if (schedule.kind === 'group' && schedule.status === 'pending') {
        nextMap.set(schedule.scheduledFor, 'waiting');
      }
    });

    return nextMap;
  }, [data.occupiedDates, data.schedules]);
  const eventDates = useMemo(() => {
    const nextSet = new Set<string>();
    data.occupiedDates.forEach((date) => nextSet.add(date));
    data.schedules.forEach((schedule) => nextSet.add(schedule.scheduledFor));
    return nextSet;
  }, [data.occupiedDates, data.schedules]);

  const filteredSchedules = useMemo(() => {
    if (!selectedDate) {
      return data.schedules;
    }
    return data.schedules.filter((schedule) => schedule.scheduledFor === selectedDate);
  }, [data.schedules, selectedDate]);

  const selectedManualItem = useMemo(
    () => filteredSchedules.find((schedule) => schedule.kind === 'manual') || null,
    [filteredSchedules],
  );
  const selectedGroupSchedules = useMemo(
    () => filteredSchedules.filter((schedule) => schedule.kind === 'group'),
    [filteredSchedules],
  );
  const selectedDateIsPast = Boolean(selectedDate && selectedDate < todayIso);

  const updateSelectedDate = useCallback((nextDate: string | null | ((current: string | null) => string | null)) => {
    setSelectedDate(nextDate);
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 960px)').matches) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
    }
  }, []);

  useEffect(() => {
    setManualReason(selectedManualItem?.reasonText || '');
  }, [selectedDate, selectedManualItem]);

  const respondToRequest = useCallback(
    async (requestId: number, response: 'accepted' | 'rejected') => {
      setRespondingRequestId(requestId);
      try {
        await api.post(`/schedules/request/${requestId}/respond`, { response });
        setBanner(response === 'accepted' ? 'Date accepted' : 'Date rejected');
        await loadCalendar();
      } catch (submitError: any) {
        setBanner(submitError?.response?.data?.message || 'Could not update schedule response');
      } finally {
        setRespondingRequestId(null);
      }
    },
    [loadCalendar],
  );

  const requestCancellation = useCallback(
    async (requestId: number) => {
      setCancellingRequestId(requestId);
      try {
        await api.post(`/schedules/request/${requestId}/cancel-request`);
        setBanner('Cancellation request sent to the group owner');
      } catch (submitError: any) {
        setBanner(submitError?.response?.data?.message || 'Could not request cancellation');
      } finally {
        setCancellingRequestId(null);
      }
    },
    [],
  );

  const reserveSelectedDate = useCallback(async () => {
    if (!selectedDate || !manualReason.trim()) {
      setBanner('Add a reason before reserving the date');
      return;
    }
    if (selectedDate < todayIso) {
      setBanner('Past dates cannot be reserved manually');
      return;
    }

    setSavingManual(true);
    try {
      await api.post('/schedules/calendar/manual', {
        occupiedDate: selectedDate,
        reasonText: manualReason.trim(),
      });
      setBanner('Date marked as occupied');
      await loadCalendar();
    } catch (submitError: any) {
      setBanner(submitError?.response?.data?.message || 'Could not reserve the date');
    } finally {
      setSavingManual(false);
    }
  }, [loadCalendar, manualReason, selectedDate, todayIso]);

  const deleteManualEvent = useCallback(
    async (manualEventId: number) => {
      const approved = await confirm({
        title: 'Make Date Vacant',
        message: 'This will clear the occupied note and make the selected date vacant again.',
        confirmLabel: 'Make Vacant',
        cancelLabel: 'Keep Occupied',
      });

      if (!approved) {
        return;
      }

      setDeletingManualId(manualEventId);
      try {
        await api.delete(`/schedules/calendar/manual/${manualEventId}`);
        setBanner('Date is vacant again');
        await loadCalendar();
      } catch (submitError: any) {
        setBanner(submitError?.response?.data?.message || 'Could not clear the date');
      } finally {
        setDeletingManualId(null);
      }
    },
    [confirm, loadCalendar],
  );

  return (
    <div className="calendar-page">
      <div className="calendar-page__header">
        <div>
          <p className="calendar-page__eyebrow">Production Calendar</p>
          <h1>Schedules</h1>
        </div>
        <button className="calendar-page__home" onClick={() => navigate('/content')} type="button">
          Home
        </button>
      </div>

      {banner ? <div className="calendar-page__banner">{banner}</div> : null}
      {error ? <div className="calendar-page__error">{error}</div> : null}

      <div className="calendar-page__layout">
        <section className="calendar-page__calendar-card">
          <div className="calendar-page__month-bar">
            <button onClick={() => setVisibleMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
              &lt;
            </button>
            <h2>{formatMonthLabel(visibleMonth)}</h2>
            <button onClick={() => setVisibleMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
              &gt;
            </button>
          </div>

          <div className="calendar-page__legend">
            <span className="calendar-page__legend-chip calendar-page__legend-chip--vacant">Vacant</span>
            <span className="calendar-page__legend-chip calendar-page__legend-chip--waiting">Waiting</span>
            <span className="calendar-page__legend-chip calendar-page__legend-chip--occupied">Occupied</span>
          </div>

          <div className="calendar-page__grid">
            {DAY_LABELS.map((label) => (
              <div key={label} className="calendar-page__day-label">
                {label}
              </div>
            ))}

            {monthCells.map((cell) => {
              const isoDate = formatIsoDate(cell.date);
              const dayStatus = dayStatusByDate.get(isoDate) || 'vacant';
              const isPastDate = isoDate < todayIso;
              const hadEvent = eventDates.has(isoDate);
              const selected = selectedDate === isoDate;

              return (
                <button
                  key={`${isoDate}-${cell.inMonth ? 'in' : 'out'}`}
                  className={[
                    'calendar-page__day',
                    cell.inMonth ? '' : 'calendar-page__day--outside',
                    `calendar-page__day--${dayStatus}`,
                    isPastDate ? 'calendar-page__day--past' : '',
                    isPastDate && hadEvent ? 'calendar-page__day--past-has-event' : '',
                    selected ? 'calendar-page__day--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => updateSelectedDate((current) => (current === isoDate ? null : isoDate))}
                  type="button"
                >
                  <span>{cell.date.getDate()}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="calendar-page__list-card">
          <div className="calendar-page__list-header">
            <div>
              <p className="calendar-page__eyebrow">Schedule List</p>
              <h2>
                {selectedDate
                  ? `Showing ${filteredSchedules.length} items on ${formatDisplayDate(selectedDate)}`
                  : 'All current schedules'}
              </h2>
            </div>
            {selectedDate ? (
              <button className="calendar-page__clear" onClick={() => updateSelectedDate(null)}>
                Clear date filter
              </button>
            ) : null}
          </div>

          {selectedDate ? (
            <div className="calendar-page__reserve-panel">
              <div>
                <p className="calendar-page__reserve-eyebrow">Selected Date</p>
                <h3>{formatDisplayDate(selectedDate)}</h3>
              </div>

              {selectedGroupSchedules.length > 0 ? (
                <p className="calendar-page__reserve-note">
                  This date is already held by a group schedule, so it stays locked here while the group decision is active.
                </p>
              ) : null}

              {selectedDateIsPast ? (
                <p className="calendar-page__reserve-note">
                  Past dates are view-only. You can review old schedule details here, but manual reservation is allowed only for today and future dates.
                </p>
              ) : null}

              <label className="calendar-page__reserve-label">
                Occupied reason
                <textarea
                  disabled={selectedGroupSchedules.length > 0 || selectedDateIsPast}
                  onChange={(event) => setManualReason(event.target.value)}
                  placeholder="Type a manual reason, story name, or scene note"
                  rows={3}
                  value={manualReason}
                />
              </label>

              <div className="calendar-page__reserve-actions">
                <button
                  disabled={savingManual || selectedGroupSchedules.length > 0 || selectedDateIsPast || !manualReason.trim()}
                  onClick={() => reserveSelectedDate().catch(() => {})}
                  type="button"
                >
                  {savingManual ? 'Saving...' : selectedManualItem ? 'Update occupied note' : 'Reserve this date'}
                </button>
                {selectedManualItem?.manualEventId ? (
                  <button
                    className="calendar-page__action--vacate"
                    disabled={deletingManualId === selectedManualItem.manualEventId}
                    onClick={() => deleteManualEvent(selectedManualItem.manualEventId as number).catch(() => {})}
                    type="button"
                  >
                    {deletingManualId === selectedManualItem.manualEventId ? 'Clearing...' : 'Make vacant again'}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {loading ? <div className="calendar-page__loading">Loading calendar...</div> : null}

          {!loading && filteredSchedules.length === 0 ? (
            <div className="calendar-page__empty">
              {selectedDate ? 'No schedule items on this date yet.' : 'No schedules found yet.'}
            </div>
          ) : null}

          {!loading && filteredSchedules.length > 0 ? (
            <div className="calendar-page__schedule-list">
              {filteredSchedules.map((schedule) => {
                const highlighted =
                  schedule.kind === 'group' && highlightedRequestId === schedule.requestId;

                return (
                  <article
                    key={schedule.kind === 'manual' ? `manual-${schedule.manualEventId}` : `group-${schedule.requestId}`}
                    className={`calendar-page__schedule-card${highlighted ? ' calendar-page__schedule-card--highlighted' : ''}`}
                  >
                    <div className="calendar-page__schedule-top">
                      <div>
                        <p className="calendar-page__story">{schedule.storyTitle}</p>
                        <h3>{schedule.sceneTitle}</h3>
                        <p className="calendar-page__meta">
                          {schedule.scheduledForLabel} - {getStatusLabel(schedule)}
                        </p>
                      </div>
                      <span className={`calendar-page__status calendar-page__status--${schedule.status}`}>
                        {schedule.status === 'manual' ? 'manual' : schedule.status}
                      </span>
                    </div>

                    {schedule.kind === 'manual' ? (
                      <p className="calendar-page__summary-row">Reserved personally by you.</p>
                    ) : (
                      <>
                        <div className="calendar-page__role-row">
                          <span className="calendar-page__role-label">Role</span>
                          <div className="calendar-page__role-tags">
                            {schedule.roleLabels.length > 0 ? (
                              schedule.roleLabels.map((label) => (
                                <span key={label} className="calendar-page__role-tag">
                                  {label}
                                </span>
                              ))
                            ) : (
                              <span className="calendar-page__role-tag">Group member</span>
                            )}
                          </div>
                        </div>

                        <div className="calendar-page__summary-row">
                          <span>{schedule.acceptedCount} accepted</span>
                          <span>{schedule.pendingCount} pending</span>
                          {schedule.status === 'pending' ? <span>Waiting for all</span> : null}
                          {schedule.rejectedBy.length > 0 ? (
                            <span>Rejected by {schedule.rejectedBy.map((person) => person.name).join(', ')}</span>
                          ) : null}
                        </div>
                      </>
                    )}

                    <div className="calendar-page__actions">
                      {schedule.kind === 'group' && schedule.storyId ? (
                        <>
                          <button onClick={() => navigate(`/story/${schedule.storyId}`)}>View Story</button>
                          <button onClick={() => navigate(`/schedule/${schedule.storyId}`)}>Open Schedule</button>
                        </>
                      ) : null}

                      {schedule.kind === 'manual' && schedule.manualEventId ? (
                        <button
                          className="calendar-page__action--vacate"
                          disabled={deletingManualId === schedule.manualEventId}
                          onClick={() => deleteManualEvent(schedule.manualEventId as number).catch(() => {})}
                        >
                          {deletingManualId === schedule.manualEventId ? 'Clearing...' : 'Make vacant again'}
                        </button>
                      ) : null}

                      {schedule.kind === 'group' && schedule.canRequestCancellation && schedule.requestId ? (
                        <button
                          disabled={cancellingRequestId === schedule.requestId}
                          onClick={() => requestCancellation(schedule.requestId as number).catch(() => {})}
                        >
                          {cancellingRequestId === schedule.requestId ? 'Sending...' : 'Request cancellation'}
                        </button>
                      ) : null}

                      {schedule.kind === 'group' && schedule.canRespond && schedule.requestId ? (
                        <>
                          <button
                            className="calendar-page__action--accept"
                            disabled={respondingRequestId === schedule.requestId}
                            onClick={() => respondToRequest(schedule.requestId as number, 'accepted').catch(() => {})}
                          >
                            Accept
                          </button>
                          <button
                            className="calendar-page__action--reject"
                            disabled={respondingRequestId === schedule.requestId}
                            onClick={() => respondToRequest(schedule.requestId as number, 'rejected').catch(() => {})}
                          >
                            Reject
                          </button>
                        </>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </section>
      </div>
      {confirmDialog}
    </div>
  );
};

export default CalendarPage;
